import * as axios from "axios";

import Api from "./Api";

import { AddFareZone } from "../types/AddFareZone";
import { AlertType } from "../types/AlertMessage";
import { DeleteFareZone } from "../types/DeleteFareZone";
import { EditFareZone } from "../types/EditFareZone";
import { FareZone, FareZoneSummary } from "../types/FareZone";
import { Review } from "../types/Review";

export default class FareZoneService extends Api {
  private _endpoint = "/FareZone";

  public async getAll(includeDraft: boolean): Promise<FareZoneSummary[]> {
    return super
      .init()
      .get<FareZoneSummary[]>(this._endpoint, {
        params: { includeDraft: includeDraft },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
        return [];
      });
  }

  public async exportFareZones(): Promise<Blob> {
    return super
      .init()
      .get<Blob>(`${this._endpoint}/ExportZones`)
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        return new Blob();
      });
  }

  public async exportMasterReport(
    fareZoneEditDate: Date,
    fareDate: Date
  ): Promise<Blob> {
    return super
      .init()
      .get<Blob>(`${this._endpoint}/ExportMaster`, {
        params: { FareZoneChangeDate: fareZoneEditDate, FareDate: fareDate },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        return new Blob();
      });
  }

  public async exportSchemeCoordinatorReport(): Promise<Blob> {
    return super
      .init()
      .get<Blob>(`${this._endpoint}/GetSchemeCoordinatorReport`)
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        return new Blob();
      });
  }

  public async getById(
    id: string,
    includeDraft: boolean
  ): Promise<FareZone | undefined> {
    return super
      .init()
      .get<FareZone>(`${this._endpoint}/${id}`, {
        params: { includeDraft: includeDraft },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
        return undefined;
      });
  }

  public async getStopsInFareZone(
    id: string,
    includeDraft: boolean
  ): Promise<any> {
    return super
      .init()
      .get<FareZoneSummary>(`${this._endpoint}/${id}/stops`, {
        params: { includeDraft: includeDraft },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
        return undefined;
      });
  }

  public async addFareZone(
    fareZone: AddFareZone
  ): Promise<FareZone | undefined> {
    return super
      .init()
      .post<FareZone>(this._endpoint, fareZone)
      .then((response) => {
        this.showAlert({
          message: "Successfully added Fare Zone",
          type: AlertType.Information,
        });
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
        return undefined;
      });
  }

  public async editFareZone(
    id: string,
    fareZone: EditFareZone
  ): Promise<boolean> {
    return super
      .init()
      .patch<FareZone>(`${this._endpoint}/${id}`, fareZone)
      .then((response) => {
        this.showAlert({
          message: "Successfully modified Fare Zone",
          type: AlertType.Information,
        });
        return true;
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
        return false;
      });
  }

  public async deleteFareZone(id: string, fareZone: DeleteFareZone) {
    return super
      .init()
      .delete<DeleteFareZone>(`${this._endpoint}/${id}`, {
        data: { lastModifiedDate: fareZone.lastModifiedDate },
      })
      .then((response) => {
        this.showAlert({
          message: "Successfully deleted Fare Zone",
          type: AlertType.Information,
        });
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
      });
  }

  public async approve(id: string, fareZone: Review) {
    return super
      .init()
      .patch<DeleteFareZone>(`${this._endpoint}/${id}/approve`, fareZone)
      .then((response) => {
        this.showAlert({
          message: "Successfully approved Fare Zone",
          type: AlertType.Information,
        });
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
      });
  }

  public async reject(id: string, fareZone: Review) {
    return super
      .init()
      .patch<DeleteFareZone>(`${this._endpoint}/${id}/reject`, fareZone)
      .then((response) => {
        this.showAlert({
          message: "Successfully approved Fare Zone",
          type: AlertType.Information,
        });
      })
      .catch((error: axios.AxiosError) => {
        this.showAlert({
          message: (error.response?.data as string) ?? error.message,
          type: AlertType.Error,
        });
      });
  }

  public async GetFareChangesReport(snapshot: Date): Promise<Blob> {
    return super
      .init()
      .get<Blob>(`${this._endpoint}/GetFarezoneFareChangesReport`, {
        params: { snapshotDate: snapshot },
      })
      .then((response) => {
        return response.data;
      })
      .catch((error: axios.AxiosError) => {
        return new Blob();
      });
  }
}
